import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Swords, Hourglass, Trophy, Crown, History, ChevronLeft, Shield, Sword, Shirt, Eye, EyeOff, Play, Skull, Share2, Megaphone, TrendingUp, Star, Scroll } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { StatBars } from "@/components/StatBars";
import { AboutBox } from "@/components/AboutBox";
import { LifecycleBadge, StreakBadge, CooldownChip, DurabilityBar } from "@/components/GladiatorCard";
import BattleReplay from "@/components/BattleReplay";
import ShareModal from "@/components/ShareModal";
import BeastIcon from "@/components/BeastIcon";
import Modal from "@/components/Modal";
import { CLASS_META, CLASS_IMAGE, TACTICS, RARITY, SKILL_META } from "@/lib/game";
import GoldenCrownIcon from "@/components/GoldenCrownIcon";
import { HelmetGlyph } from "@/components/HelmetGlyph";

const HelmetSlotIcon = ({ size = 15, className = "" }) => <HelmetGlyph size={size} className={className} />;

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

function fmtCooldown(seconds) {
  const minutes = Math.ceil((seconds || 0) / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

// Star rating display for individual public-profile combat stats.
// Every completed 50 points above 1000 replaces half a leftmost yellow star with red.
function StatStars({ value, statKey }) {
  const numericValue = Math.max(0, Number(value) || 0);
  const yellowStars = numericValue <= 50 ? 0.5 : Math.min(10, Math.ceil(numericValue / 50) * 0.5);
  const redStars = numericValue > 1000 ? Math.min(10, Math.floor((numericValue - 1000) / 50) * 0.5) : 0;

  return (
    <div className="flex items-center gap-0.5 flex-wrap shrink-0" data-testid={`stat-stars-${statKey}`} title={numericValue > 1000 ? `${numericValue}: red fill marks points above 1000` : `${numericValue}`}>
      {[...Array(10)].map((_, index) => {
        const yellowFill = Math.min(1, Math.max(0, yellowStars - index));
        const redFill = Math.min(1, Math.max(0, redStars - index));
        return (
          <div key={index} className="relative shrink-0" data-testid={`stat-star-${statKey}-${index + 1}`}>
            <Star size={12} className="text-zinc-700" />
            {yellowFill > 0 && (
              <div className="absolute inset-0 overflow-hidden" style={{ width: `${yellowFill * 100}%` }}>
                <Star size={12} className="text-amber-400 fill-amber-400" />
              </div>
            )}
            {redFill > 0 && (
              <div data-testid={`stat-overflow-fill-${statKey}-${index + 1}`} className="absolute inset-0 pointer-events-none">
                <div className="h-full overflow-hidden" style={{ width: `${redFill * 100}%` }}>
                  <Star size={12} className="text-red-500 fill-red-600" />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Public stat display - shows stars instead of bars
function PublicStatDisplay({ skills }) {
  if (!skills) return null;
  
  const stats = [
    { key: 'strength', label: 'Strength', value: skills.strength || 0 },
    { key: 'stamina', label: 'Stamina', value: skills.stamina || 0 },
    { key: 'speed', label: 'Speed', value: skills.speed || 0 },
    { key: 'technique', label: 'Technique', value: skills.technique || 0 },
    { key: 'precision', label: 'Precision', value: skills.precision || 0 },
    { key: 'defense', label: 'Defense', value: skills.defense || 0 },
  ];

  return (
    <div className="space-y-2" data-testid="public-stat-display">
      {stats.map(({ key, label, value }) => (
        <div key={key} className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm text-zinc-300 min-w-[80px] shrink-0">{label}</span>
          <StatStars value={value} statKey={key} />
        </div>
      ))}
    </div>
  );
}

function LoadoutSlot({ label, Icon, item, isMine }) {
  const meta = item ? (RARITY[item.rarity] || RARITY.common) : null;
  return (
    <div data-testid={`loadout-${label.toLowerCase()}`} className={`border p-3 ${item ? meta.border + " " + meta.bg : "border-zinc-800 bg-zinc-900/30"}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-1"><Icon size={12} /> {label}</div>
      {item ? (
        <>
          <div className={`text-sm font-cinzel ${meta.text}`}>{item.name}</div>
          <div className="flex flex-wrap gap-2 mt-1">
            {Object.entries(item.mods || {}).map(([k, v]) => <span key={k} className="text-[10px] text-emerald-400">+{v} {SKILL_META[k]?.label || k}</span>)}
          </div>
          {(item.affixes || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {item.affixes.map((af, i) => <span key={i} className="text-[10px] text-violet-300 border border-violet-800/40 bg-violet-950/20 px-1.5 py-0.5 rounded-sm">{af.display}</span>)}
            </div>
          )}
          {isMine && <DurabilityBar value={item.durability} />}
        </>
      ) : <div className="text-xs text-zinc-600">— empty —</div>}
    </div>
  );
}

export default function GladiatorProfile() {
  const { gladId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const profileRequestRef = useRef(0);
  const [mine, setMine] = useState([]);
  const [chalOpen, setChalOpen] = useState(false);
  const [myGlad, setMyGlad] = useState("");
  const [tactics, setTactics] = useState("aggressive");  // Default to first option in TACTICS array
  const [busy, setBusy] = useState(false);
  const [showLoadout, setShowLoadout] = useState(true);
  const [replayId, setReplayId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [bounty, setBounty] = useState(null);
  const [bountyOpen, setBountyOpen] = useState(false);
  const [bountyAmt, setBountyAmt] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [instantScoutOpen, setInstantScoutOpen] = useState(false);
  const [instantScoutCount, setInstantScoutCount] = useState(0);
  const [fightTab, setFightTab] = useState("career"); // career, tournaments, scrimmages
  
  // Per-tab pagination state
  const [tabData, setTabData] = useState({
    career: { fights: [], page: 1, hasMore: false, loading: false, loaded: false },
    tournament: { fights: [], page: 1, hasMore: false, loading: false, loaded: false },
    scrimmage: { fights: [], page: 1, hasMore: false, loading: false, loaded: false }
  });

  const loadBounty = () => api.get(`/bounties/gladiator/${gladId}`).then((r) => setBounty(r.data)).catch(() => setBounty(null));

  const loadInstantScoutCount = async () => {
    try {
      const { data } = await api.get("/inventory");
      const item = (data.consumables || []).find(c => c.id === "instant_scout");
      setInstantScoutCount(item?.count || 0);
    } catch (e) {
      setInstantScoutCount(0);
    }
  };

  const load = async () => {
    const requestId = ++profileRequestRef.current;
    setData(null);
    setLoadError("");
    setHistoryPage(1);
    setFightTab("career");
    setTabData({
      career: { fights: [], page: 1, hasMore: false, loading: false, loaded: false },
      tournament: { fights: [], page: 1, hasMore: false, loading: false, loaded: false },
      scrimmage: { fights: [], page: 1, hasMore: false, loading: false, loaded: false }
    });
    
    try {
      // Load the profile and visible tab together. Other history is fetched only when opened.
      const { data: mainData } = await api.get(`/gladiators/${gladId}/public?fight_type=career&page=1&limit=10`);
      if (requestId !== profileRequestRef.current) return;
      setData(mainData);
      setShowLoadout(mainData.gladiator.show_loadout !== false);
      setTabData({
        career: { fights: mainData.history, page: 1, hasMore: mainData.has_more, loading: false, loaded: true },
        tournament: { fights: [], page: 1, hasMore: false, loading: false, loaded: false },
        scrimmage: { fights: [], page: 1, hasMore: false, loading: false, loaded: false }
      });
    } catch (e) {
      if (requestId !== profileRequestRef.current) return;
      setData(false);
      if (e?.response?.status !== 404) setLoadError(apiErr(e, "Unable to load this gladiator right now."));
    }
    if (requestId === profileRequestRef.current) {
      loadBounty();
      loadInstantScoutCount();
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); window.scrollTo(0, 0); }, [gladId]);

  const selectFightTab = async (tab) => {
    setFightTab(tab);
    const type = tab === "tournaments" ? "tournament" : tab === "scrimmages" ? "scrimmage" : "career";
    const currentTab = tabData[type];
    if (currentTab.loaded || currentTab.loading) return;

    setTabData((previous) => ({ ...previous, [type]: { ...previous[type], loading: true } }));
    try {
      const { data: result } = await api.get(`/gladiators/${gladId}/public?fight_type=${type}&page=1&limit=10`);
      setTabData((previous) => ({
        ...previous,
        [type]: { fights: result.history, page: 1, hasMore: result.has_more, loading: false, loaded: true }
      }));
    } catch (e) {
      toast.error(apiErr(e, "Unable to load this fight history."));
      setTabData((previous) => ({ ...previous, [type]: { ...previous[type], loading: false } }));
    }
  };

  const loadMoreFights = async (type) => {
    const currentTab = tabData[type];
    if (currentTab.loading || !currentTab.hasMore) return;
    
    setTabData(prev => ({
      ...prev,
      [type]: { ...prev[type], loading: true }
    }));
    
    try {
      const nextPage = currentTab.page + 1;
      const { data: result } = await api.get(`/gladiators/${gladId}/public?fight_type=${type}&page=${nextPage}&limit=10`);
      
      setTabData(prev => ({
        ...prev,
        [type]: {
          fights: [...prev[type].fights, ...result.history],
          page: nextPage,
          hasMore: result.has_more,
          loading: false
        }
      }));
    } catch (e) {
      toast.error(apiErr(e));
      setTabData(prev => ({
        ...prev,
        [type]: { ...prev[type], loading: false }
      }));
    }
  };

  const placeBounty = async () => {
    const amt = parseInt(bountyAmt, 10);
    if (!amt || amt < (bounty?.min_bounty || 1)) return toast.error(`Minimum bounty is ${bounty?.min_bounty || 1} gold`);
    setBusy(true);
    try {
      await api.post("/bounties", { target_glad: gladId, amount: amt });
      toast.success(`Bounty of ${amt} gold placed on ${data.gladiator.name}!`, { icon: <GoldCoin size={18} /> });
      setBountyOpen(false); setBountyAmt("");
      loadBounty();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleLoadout = async () => {
    setBusy(true);
    try {
      const ns = !showLoadout;
      await api.post(`/gladiators/${gladId}/loadout-visibility`, { show: ns });
      setShowLoadout(ns);
      toast.success(ns ? "Loadout is now public" : "Loadout hidden from others");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const openChallenge = async () => {
    try {
      const { data: g } = await api.get("/gladiators");
      setMine(g.active.filter((x) => x.fight_ready && !x.auctioned));
      setChalOpen(true);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const useInstantScout = async () => {
    if (instantScoutCount < 1) {
      toast.error("You don't have any Instant Scout items");
      return;
    }
    setBusy(true);
    try {
      const { data: result } = await api.post(`/gladiators/${gladId}/instant-scout`);
      const potentialGrade = result.gladiator.potential_grade || result.gladiator.potential_band?.label || 'Unknown';
      toast.success(`${result.gladiator.name} fully scouted! Potential: ${potentialGrade}`);
      setData({ ...data, gladiator: result.gladiator });
      setInstantScoutCount(result.remaining_items);
      setInstantScoutOpen(false);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const sendChallenge = async () => {
    if (!myGlad) return toast.error("Pick one of your gladiators");
    setBusy(true);
    try {
      await api.post("/challenges", { my_gladiator_id: myGlad, opponent_gladiator_id: gladId, tactics });
      toast.success("Challenge sent to opponent's inbox");
      setChalOpen(false); setMyGlad("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (data === null) return <Loader />;
  if (data === false) {
    if (!loadError) return <Empty label="Gladiator not found." />;
    return (
      <div data-testid="gladiator-profile-load-error" className="border border-red-900/50 bg-red-950/20 p-6 text-center">
        <p data-testid="gladiator-profile-load-error-message" className="text-sm text-red-200">{loadError}</p>
        <button data-testid="gladiator-profile-retry-button" onClick={load} className="mt-4 border border-amber-700/60 px-4 py-2 text-sm text-amber-200 transition-colors hover:bg-amber-900/30">Try again</button>
      </div>
    );
  }

  const g = data.gladiator;
  const meta = CLASS_META[g.cls] || {};
  const img = CLASS_IMAGE[g.cls];
  const total = g.wins + g.losses;
  const winRate = total ? Math.round((g.wins / total) * 100) : 0;
  const canChallenge = !data.is_mine && !g.retired;
  const crownStatus = g.bronze_crown ? "Bronze Crown Holder" : g.silver_crown ? "Silver Crown Holder" : g.golden_crown ? "Golden Crown Holder" : null;

  // Use backend-calculated fight records (all fights, not just loaded ones)
  const careerWins = data.fight_records?.career?.wins || 0;
  const careerLosses = data.fight_records?.career?.losses || 0;
  const tournamentWins = data.fight_records?.tournament?.wins || 0;
  const tournamentLosses = data.fight_records?.tournament?.losses || 0;
  const scrimmageWins = data.fight_records?.scrimmage?.wins || 0;
  const scrimmageLosses = data.fight_records?.scrimmage?.losses || 0;
  
  // Use per-tab loaded fights
  const careerFights = tabData.career.fights;
  const tournamentFights = tabData.tournament.fights;
  const scrimmageFights = tabData.scrimmage.fights;

  return (
    <div data-testid="gladiator-profile">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-zinc-500 hover:text-amber-400 text-sm mb-4 transition-colors">
        <ChevronLeft size={16} /> Back
      </button>

      <div data-testid="gladiator-profile-hero" className="relative overflow-hidden border border-amber-800/40 bg-zinc-950 mb-8">
        {img && (
          <div className="absolute inset-0">
            <img src={img} alt={g.cls} className="w-full h-full object-cover object-top opacity-30" data-testid="class-image" />
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/40" />
          </div>
        )}
        <div className="relative p-5 sm:p-7 lg:p-9 grid gap-6 lg:grid-cols-[10rem_minmax(0,1.2fr)_minmax(12rem,.8fr)_minmax(13rem,.9fr)] lg:gap-8 items-start">
          <aside data-testid="profile-portrait-column" className="w-full max-w-[10rem] lg:max-w-none">
            {img && <img data-testid="profile-portrait" src={img} alt={g.cls} className="w-full aspect-square object-cover object-top border border-amber-700/60 shadow-lg" />}
            {data.equipped_beast && (
              <div data-testid="profile-beast-strip" className="mt-3 border-t border-teal-800/50 pt-3 text-left">
                <div className="flex items-center gap-2">
                  <BeastIcon size={26} species={data.equipped_beast.species} />
                  <div className="min-w-0">
                    <p className="font-cinzel text-xs text-teal-200 truncate">{data.equipped_beast.custom_name || data.equipped_beast.display_name}</p>
                    <p className="text-[11px] text-zinc-500">+{data.equipped_beast.primary_value} {data.equipped_beast.primary_stat}{data.equipped_beast.affix_stat && data.equipped_beast.affix_value ? ` · +${data.equipped_beast.affix_value}% ${data.equipped_beast.affix_stat}` : ""}</p>
                    {data.equipped_beast.legendary_custom_affix_stat && (
                      <p data-testid="profile-beast-custom-affix" className="text-[11px] text-amber-300">Custom +{data.equipped_beast.legendary_custom_affix_value || 5}% {data.equipped_beast.legendary_custom_affix_stat}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </aside>

          <section data-testid="profile-identity-group" className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">{meta.icon && <meta.icon className={meta.color} size={18} />}<span className="text-[11px] uppercase tracking-widest text-zinc-400">{g.cls}</span><LifecycleBadge lifecycle={g.lifecycle} /></div>
            <h1 className="font-cinzel text-3xl sm:text-4xl leading-tight text-amber-300 uppercase tracking-wide break-words">{g.name}</h1>
            {g.owner_campaign_title && <p data-testid="profile-campaign-title" className="text-sm text-amber-200/90 mt-2 font-cinzel">{g.owner_campaign_title}</p>}
            <p className="text-sm text-zinc-500 mt-2">Lanista: <button data-testid="profile-owner-link" onClick={() => navigate(`/profile/${g.owner_id}`)} className="text-zinc-300 hover:text-amber-400">{g.owner_name || "Unknown"}</button></p>
            {data.alliance && <p data-testid="profile-alliance" className="text-sm text-zinc-500 mt-1">Alliance: <button data-testid="profile-alliance-link" onClick={() => navigate(`/alliance/${data.alliance.id}`)} className="text-amber-300/90 hover:text-amber-400">{data.alliance.name}{data.alliance.tag ? ` [${data.alliance.tag}]` : ""}</button></p>}
            {crownStatus && <p data-testid="profile-crown-status" className="mt-3 text-xs uppercase tracking-widest text-amber-300">{crownStatus}</p>}
            {g.crown_first_defense_pending && <p data-testid="profile-crown-promotion-status" className="mt-2 text-xs text-amber-200">First crown defense decides promotion: win to advance; lose and earn another crown chance.</p>}
            {g.battle_cry_text && <p data-testid="profile-battle-cry" className="mt-4 text-sm text-amber-200/90 italic break-words">“{g.battle_cry_text}”</p>}
          </section>

          <section data-testid="profile-standing-group" className="min-w-0 border-t border-amber-800/40 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <h2 className="font-cinzel text-xs uppercase tracking-widest text-amber-400">Competitive standing</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Rating</dt><dd className="mt-0.5 text-lg text-amber-300">{g.rating}</dd></div>
              {data.rank && <div><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Global rank</dt><dd className="mt-0.5 text-base text-zinc-100">#{data.rank}</dd></div>}
              {data.elo_division_rank && <div data-testid="gladiator-elo-division-rank"><dt className="text-[10px] uppercase tracking-widest text-zinc-600">{data.elo_division_rank.name}</dt><dd className="mt-0.5 text-base text-stone-100">Rank #{data.elo_division_rank.rank}</dd></div>}
              {data.bracket_rank && <div data-testid="gladiator-bracket-rank"><dt className="text-[10px] uppercase tracking-widest text-zinc-600">{data.bracket_rank.name}</dt><dd className="mt-0.5 text-base text-stone-100">Rank #{data.bracket_rank.rank}</dd></div>}
              {data.peak_rank && data.rank && <div><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Highest rank</dt><dd className="mt-0.5 text-zinc-300">#{data.peak_rank}</dd></div>}
              {g.crown_wins > 0 && <div data-testid="profile-crown-wins"><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Crown wins</dt><dd className="mt-0.5 text-amber-300">{g.crown_wins}</dd></div>}
            </dl>
          </section>

          <section data-testid="profile-condition-group" className="min-w-0 border-t border-amber-800/40 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <h2 className="font-cinzel text-xs uppercase tracking-widest text-amber-400">Career condition</h2>
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Age</dt><dd className="mt-0.5 text-zinc-100">{g.age}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Potential</dt><dd className="mt-0.5 text-violet-300">{g.potential_grade}</dd></div>
              <div className="col-span-2"><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Record</dt><dd className="mt-0.5 text-zinc-100"><span className="text-emerald-400">{g.wins}W</span> / <span className="text-red-400">{g.losses}L</span> <span className="text-zinc-500">({winRate}%)</span></dd></div>
              {g.decisive?.decisive_pct != null && <div className="col-span-2" data-testid="profile-decisive-pct"><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Decisive wins</dt><dd className="mt-0.5 text-orange-300">{g.decisive.decisive_pct}% <span className="text-zinc-500">({g.decisive.decisive_wins})</span></dd></div>}
              {(careerWins > 0 || careerLosses > 0) && <div className="col-span-2" data-testid="career-record"><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Career record</dt><dd className="mt-0.5 text-zinc-300"><span className="text-emerald-400">{careerWins}W</span> / <span className="text-red-400">{careerLosses}L</span></dd></div>}
              {g.recruited_at && <div className="col-span-2" data-testid="profile-recruited-date"><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Recruited</dt><dd className="mt-0.5 text-zinc-300">{fmtDate(g.recruited_at)}</dd></div>}
              {bounty?.total > 0 && <div className="col-span-2" data-testid="profile-bounty-badge"><dt className="text-[10px] uppercase tracking-widest text-zinc-600">Bounty</dt><dd className="mt-0.5 text-yellow-300">{bounty.total}g <span className="text-zinc-500">({bounty.count})</span></dd></div>}
            </dl>
          </section>

          <div data-testid="profile-actions" className="lg:col-start-2 lg:col-span-3 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 border-t border-zinc-800 pt-5">
            {!g.retired && g.streak >= 2 && <StreakBadge streak={g.streak} testId="profile-streak-badge" />}
            {!g.retired && !g.fight_ready && <span data-testid="profile-fight-cooldown" className="text-xs uppercase tracking-widest text-zinc-500">Fight ready in {fmtCooldown(g.fight_cooldown_seconds)}</span>}
            {canChallenge && <button data-testid="challenge-btn" className={btn() + " w-full sm:w-auto justify-center"} onClick={openChallenge}>Challenge</button>}
            {bounty?.active && !data.is_mine && !g.retired && <button data-testid="place-bounty-btn" onClick={() => setBountyOpen(true)} className="inline-flex items-center justify-center px-4 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-yellow-600 text-zinc-950 hover:bg-yellow-500 transition-colors w-full sm:w-auto">Place Bounty</button>}
            {data.is_mine && <span className="text-[11px] uppercase tracking-widest text-amber-500/80">Your gladiator</span>}
            {g.retired && <span className="text-[11px] uppercase tracking-widest text-zinc-500">Retired — no longer fights</span>}
            <button data-testid="share-gladiator-btn" onClick={() => setShareOpen(true)} className="inline-flex items-center justify-center px-4 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-700/60 text-amber-300 hover:bg-amber-900/30 transition-colors w-full sm:w-auto">Share</button>
            {data.is_mine && !g.retired && !g.potential_band?.exact && instantScoutCount > 0 && <button data-testid="instant-scout-btn" className="inline-flex items-center justify-center px-4 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-orange-600 text-white hover:bg-orange-500 transition-colors shadow-lg w-full sm:w-auto" onClick={() => setInstantScoutOpen(true)} disabled={busy}>Instant Scout ({instantScoutCount})</button>}
          </div>
        </div>
      </div>

      {/* Tournament trophies — shown above the About section */}
      {(g.trophies && g.trophies.length > 0) && (
        <div className="mb-8 bg-gradient-to-br from-amber-950/30 to-zinc-950 border border-amber-700/40 p-5" data-testid="glad-trophies">
          <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><Trophy size={15} /> Tournament Trophies</h3>
          <div className="flex flex-wrap gap-2">
            {g.trophies.map((t, i) => (
              <span key={i} data-testid={`trophy-${i}`}
                className="flex items-center gap-1.5 text-xs text-amber-200 bg-amber-900/20 border border-amber-700/40 px-3 py-1.5 rounded-sm">
                <Trophy size={13} className="text-amber-400" /> {t.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Loadout (owner can toggle public visibility) */}
      <AboutBox about={g.about} canEdit={data.is_mine} endpoint={`/gladiators/${gladId}/about`}
        onSaved={(about) => setData((p) => ({ ...p, gladiator: { ...p.gladiator, about } }))}
        placeholder="Describe this gladiator's story, fighting style, or legend…" testid="glad-about" />

      {data.rivals?.length > 0 && (
        <div className="mb-8" data-testid="rivals-section">
          <h2 className="font-cinzel text-red-400 uppercase tracking-wide text-sm flex items-center gap-2 mb-3">
            <Swords size={15} /> Arena Rivals
          </h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {data.rivals.map((r) => (
              <button key={r.opponent_id} data-testid={`rival-${r.opponent_id}`}
                onClick={() => navigate(`/gladiator/${r.opponent_id}`)}
                className="text-left border border-red-900/50 bg-gradient-to-br from-red-950/25 to-zinc-950 rounded-sm p-3 hover:border-red-600/70 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-red-200 hover:text-red-100 truncate">{r.name}{r.retired ? " †" : ""}</span>
                  <span className="text-[10px] uppercase tracking-wider text-red-400/80 border border-red-500/40 bg-red-950/40 px-1.5 py-0.5 rounded-sm shrink-0">{r.meetings} battles</span>
                </div>
                <div className="mt-1.5 text-[12px] text-zinc-400 tabular-nums">
                  Head-to-head <span className="text-emerald-400 font-semibold">{r.wins}W</span> – <span className="text-red-400 font-semibold">{r.losses}L</span>
                  <span className="text-zinc-600"> for {g.name}</span>
                </div>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">Rivals are opponents faced 10+ times in the arena. A gladiator keeps their 3 fiercest.</p>
        </div>
      )}

      {(showLoadout || data.is_mine) ? (
        <div className="mb-8" data-testid="loadout-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2"><Sword size={15} /> Loadout</h2>
            {data.is_mine && (
              <button data-testid="toggle-loadout-btn" onClick={toggleLoadout} disabled={busy}
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-2.5 py-1 transition-colors">
                {showLoadout ? <><Eye size={13} /> Public</> : <><EyeOff size={13} /> Hidden</>}
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <LoadoutSlot label="Weapon" Icon={Sword} item={g.equipment_items?.weapon} isMine={data.is_mine} />
            <LoadoutSlot label="Offhand" Icon={Shield} item={g.equipment_items?.offhand} isMine={data.is_mine} />
            <LoadoutSlot label="Armor" Icon={Shirt} item={g.equipment_items?.armor} isMine={data.is_mine} />
            <LoadoutSlot label="Helmet" Icon={HelmetSlotIcon} item={g.equipment_items?.helmet} isMine={data.is_mine} />
          </div>
          {data.is_mine && !showLoadout && <p className="text-[11px] text-zinc-600 mt-2">Only you can see this loadout — other lanistae see it as hidden.</p>}
        </div>
      ) : (
        <p data-testid="loadout-hidden" className="text-xs text-zinc-600 italic mb-8">This lanista keeps {g.name}’s loadout secret.</p>
      )}


      {g.scars && g.scars.length > 0 && (
        <div className="mb-8" data-testid="glad-profile-scars">
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2 mb-3"><Skull size={15} /> Battle Scars <span className="text-zinc-600 normal-case tracking-normal">({g.scars.length})</span></h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {g.scars.map((sc) => (
              <div key={sc.id} data-testid={`profile-scar-${sc.id}`} className="border border-red-900/40 bg-red-950/15 px-3 py-2 rounded-sm">
                <div className="text-sm text-red-200 font-cinzel">{sc.name}</div>
                <div className="text-[11px] tabular-nums mt-0.5">
                  <span className="text-red-400">−{sc.pct}% {sc.neg}</span> · <span className="text-emerald-400">+{sc.pct}% {sc.pos}</span>
                </div>
                <div className="text-[10px] text-orange-400 mt-1">−2% training speed</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats + career */}
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="min-w-0">
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-4 flex items-center gap-2">
            <Shield size={15} /> Combat Skills{" "}
            {!data.is_mine ? (
              <span className="text-zinc-600 normal-case tracking-normal">(public view)</span>
            ) : (
              <span className="text-zinc-600 normal-case tracking-normal">(with gear)</span>
            )}
          </h2>
          {g.skills_hidden ? (
            <div data-testid="skills-hidden" className="bg-zinc-950 border border-dashed border-amber-900/40 p-8 text-center">
              <EyeOff size={22} className="text-zinc-600 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">This lanista keeps {g.name}'s skills out of public view.</p>
            </div>
          ) : (
            // FORCED PUBLIC VIEW - ALWAYS SHOW STARS
            <div data-testid="public-skill-stars" className="bg-zinc-950 border border-amber-900/30 p-5">
              <PublicStatDisplay skills={g.combat_skills || g.effective_skills || g.skills} />
            </div>
          )}
        </div>

        <div className="min-w-0">
          {/* Fight History Tabs */}
          <div className="flex gap-2 mb-4 border-b border-zinc-800 overflow-x-auto">
            {[
              ["career", "Career Fights"],
              ["tournaments", "Tournaments"],
              ["scrimmages", "Scrimmages"]
            ].map(([id, label]) => (
              <button
                key={id}
                data-testid={`gladiator-history-tab-${id}`}
                onClick={() => selectFightTab(id)}
                className={`px-4 py-2 font-cinzel uppercase tracking-wide text-sm transition-colors border-b-2 whitespace-nowrap shrink-0 ${
                  fightTab === id
                    ? "border-amber-500 text-amber-300"
                    : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Career Fights (Ranked PvP) */}
          {fightTab === "career" && (() => {
            // Use the correctly filtered careerFights from above (line 241)
            return (
              <>
                <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-4 flex items-center gap-2">
                  <Swords size={15} /> Ranked PvP Battles <span className="text-zinc-600 normal-case tracking-normal">({careerWins + careerLosses})</span>
                </h2>
                {careerFights.length === 0 ? (
                  <Empty label="No ranked fights yet." />
                ) : (
                  <>
                    <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="fight-history-career">
                      {careerFights.map((h) => (
                        <button key={h.battle_id} data-testid={`fight-${h.battle_id}`} onClick={() => setReplayId(h.battle_id)}
                          className={`w-full text-left flex items-center gap-3 border px-4 py-3 transition-colors hover:border-amber-600/60 ${h.won ? "border-emerald-900/40 bg-emerald-950/10 hover:bg-emerald-950/20" : "border-red-900/40 bg-red-950/10 hover:bg-red-950/20"}`}
                          title="Watch the play-by-play">
                          <span className={`text-[10px] font-bold uppercase tracking-widest w-9 shrink-0 ${h.won ? "text-emerald-400" : "text-red-400"}`}>{h.won ? "WIN" : "LOSS"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-200 truncate">
                              vs {h.opponent_id
                                ? <span onClick={(e) => { e.stopPropagation(); navigate(`/gladiator/${h.opponent_id}`); }} className="text-zinc-100 hover:text-amber-400 cursor-pointer">{h.opponent}</span>
                                : <span className="text-zinc-100">{h.opponent}</span>}
                              {h.crown_battle && (
                                <span data-testid={`crown-badge-${h.battle_id}`} title={`${h.crown_name || "Crown"} battle`}
                                  className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-yellow-300 border border-yellow-700/50 bg-yellow-950/40 px-1.5 py-0.5 align-middle">
                                  <GoldenCrownIcon size={9} /> Crown
                                </span>
                              )}
                              {h.scarred && (
                                <span data-testid={`scarred-badge-${h.battle_id}`} title={h.scar_note ? `Scarred in this fight — ${h.scar_note}` : "This gladiator was scarred in this fight"}
                                  className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-red-300 border border-red-800/50 bg-red-950/40 px-1.5 py-0.5 align-middle">
                                  <Skull size={9} /> Scarred
                                </span>
                              )}
                            </div>
                            {h.headline && <div className="text-[11px] text-zinc-500 italic truncate">{h.headline}</div>}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[11px] text-zinc-500 flex items-center gap-1 justify-end"><Play size={10} /> {h.rounds} rds</div>
                            <div className="text-[10px] text-zinc-600">{fmtDate(h.created_at)}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                    
                    {/* Load More Button for Career Tab */}
                    {tabData.career.hasMore && (
                      <button
                        data-testid="load-more-career-btn"
                        onClick={() => loadMoreFights('career')}
                        disabled={tabData.career.loading}
                        className="mt-3 w-full px-4 py-2.5 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-700/60 text-amber-300 hover:bg-amber-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {tabData.career.loading ? "Loading..." : "Load More Fights"}
                      </button>
                    )}
                  </>
                )}
              </>
            );
          })()}

          {/* Tournament Fights */}
          {fightTab === "tournaments" && (() => {
            // Use the correctly filtered tournamentFights from above (line 239)
            return (
              <>
                <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-4 flex items-center gap-2">
                  <Trophy size={15} /> Tournament Battles <span className="text-zinc-600 normal-case tracking-normal">({tournamentWins + tournamentLosses})</span>
                </h2>
                {tournamentFights.length === 0 ? (
                  <Empty label="No tournament fights yet." />
                ) : (
                  <>
                    <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="fight-history-tournaments">
                      {tournamentFights.map((h) => (
                        <button key={h.battle_id} data-testid={`fight-${h.battle_id}`} onClick={() => setReplayId(h.battle_id)}
                          className={`w-full text-left flex items-center gap-3 border px-4 py-3 transition-colors hover:border-amber-600/60 ${h.won ? "border-emerald-900/40 bg-emerald-950/10 hover:bg-emerald-950/20" : "border-red-900/40 bg-red-950/10 hover:bg-red-950/20"}`}
                          title="Watch the play-by-play">
                          <span className={`text-[10px] font-bold uppercase tracking-widest w-9 shrink-0 ${h.won ? "text-emerald-400" : "text-red-400"}`}>{h.won ? "WIN" : "LOSS"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-200 truncate">
                              vs {h.opponent_id
                                ? <span onClick={(e) => { e.stopPropagation(); navigate(`/gladiator/${h.opponent_id}`); }} className="text-zinc-100 hover:text-amber-400 cursor-pointer">{h.opponent}</span>
                                : <span className="text-zinc-100">{h.opponent}</span>}
                              <span data-testid={`tournament-badge-${h.battle_id}`} title={h.tournament ? `Tournament: ${h.tournament}` : "Tournament fight"}
                                className={`ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest border px-1.5 py-0.5 align-middle ${
                                  h.tournament?.includes('Alliance Wars') 
                                    ? 'text-purple-300 border-purple-700/50 bg-purple-950/40' 
                                    : 'text-amber-300 border-amber-700/50 bg-amber-950/40'
                                }`}>
                                {h.tournament?.includes('Alliance Wars') ? (
                                  <>
                                    <Shield size={9} /> {h.tournament}
                                  </>
                                ) : (
                                  <>
                                    <Trophy size={9} /> {h.tournament || "Tournament"}
                                  </>
                                )}
                              </span>
                              {h.scarred && (
                                <span data-testid={`scarred-badge-${h.battle_id}`} title={h.scar_note ? `Scarred in this fight — ${h.scar_note}` : "This gladiator was scarred in this fight"}
                                  className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-red-300 border border-red-800/50 bg-red-950/40 px-1.5 py-0.5 align-middle">
                                  <Skull size={9} /> Scarred
                                </span>
                              )}
                            </div>
                            {h.headline && <div className="text-[11px] text-zinc-500 italic truncate">{h.headline}</div>}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[11px] text-zinc-500 flex items-center gap-1 justify-end"><Play size={10} /> {h.rounds} rds</div>
                            <div className="text-[10px] text-zinc-600">{fmtDate(h.created_at)}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                    
                    {/* Load More Button for Tournaments Tab */}
                    {tabData.tournament.hasMore && (
                      <button
                        data-testid="load-more-tournaments-btn"
                        onClick={() => loadMoreFights('tournament')}
                        disabled={tabData.tournament.loading}
                        className="mt-3 w-full px-4 py-2.5 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-700/60 text-amber-300 hover:bg-amber-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {tabData.tournament.loading ? "Loading..." : "Load More Fights"}
                      </button>
                    )}
                  </>
                )}
              </>
            );
          })()}

          {/* Scrimmage Fights */}
          {fightTab === "scrimmages" && (() => {
            // Use the correctly filtered scrimmageFights from above (line 240)
            return (
              <>
                <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-4 flex items-center gap-2">
                  <Swords size={15} /> Friendly Scrimmages <span className="text-zinc-600 normal-case tracking-normal">({scrimmageWins + scrimmageLosses})</span>
                </h2>
                {scrimmageFights.length === 0 ? (
                  <Empty label="No scrimmage fights yet." />
                ) : (
                  <>
                    <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="fight-history-scrimmages">
                      {scrimmageFights.map((h) => (
                        <button key={h.battle_id} data-testid={`fight-${h.battle_id}`} onClick={() => setReplayId(h.battle_id)}
                          className={`w-full text-left flex items-center gap-3 border px-4 py-3 transition-colors hover:border-amber-600/60 ${h.won ? "border-emerald-900/40 bg-emerald-950/10 hover:bg-emerald-950/20" : "border-red-900/40 bg-red-950/10 hover:bg-red-950/20"}`}
                          title="Watch the play-by-play">
                          <span className={`text-[10px] font-bold uppercase tracking-widest w-9 shrink-0 ${h.won ? "text-emerald-400" : "text-red-400"}`}>{h.won ? "WIN" : "LOSS"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-200 truncate">
                              vs {h.opponent_id
                                ? <span onClick={(e) => { e.stopPropagation(); navigate(`/gladiator/${h.opponent_id}`); }} className="text-zinc-100 hover:text-amber-400 cursor-pointer">{h.opponent}</span>
                                : <span className="text-zinc-100">{h.opponent}</span>}
                              <span data-testid={`exhibition-badge-${h.battle_id}`} title="Scrimmage match — does not affect rankings"
                                className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-sky-300 border border-sky-700/50 bg-sky-950/40 px-1.5 py-0.5 align-middle">
                                <Swords size={9} /> Scrimmage
                              </span>
                              {(h.daily_duel || h.battle_tag === "Daily Duel") && <span data-testid={`daily-duel-badge-${h.battle_id}`} className="ml-2 inline-flex border border-amber-700/60 bg-amber-950/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-200 align-middle">Daily Duel</span>}
                              {h.scarred && (
                                <span data-testid={`scarred-badge-${h.battle_id}`} title={h.scar_note ? `Scarred in this fight — ${h.scar_note}` : "This gladiator was scarred in this fight"}
                                  className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-red-300 border border-red-800/50 bg-red-950/40 px-1.5 py-0.5 align-middle">
                                  <Skull size={9} /> Scarred
                                </span>
                              )}
                            </div>
                            {h.headline && <div className="text-[11px] text-zinc-500 italic truncate">{h.headline}</div>}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[11px] text-zinc-500 flex items-center gap-1 justify-end"><Play size={10} /> {h.rounds} rds</div>
                            <div className="text-[10px] text-zinc-600">{fmtDate(h.created_at)}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                    
                    {/* Load More Button for Scrimmages Tab */}
                    {tabData.scrimmage.hasMore && (
                      <button
                        data-testid="load-more-scrimmages-btn"
                        onClick={() => loadMoreFights('scrimmage')}
                        disabled={tabData.scrimmage.loading}
                        className="mt-3 w-full px-4 py-2.5 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-700/60 text-amber-300 hover:bg-amber-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {tabData.scrimmage.loading ? "Loading..." : "Load More Fights"}
                      </button>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Challenge modal */}
      {chalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setChalOpen(false)}>
          <div className="bg-zinc-950 border border-amber-800/50 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-cinzel text-xl text-amber-300 uppercase tracking-wide mb-4">Challenge {g.name}</h3>
            {mine.length === 0 ? (
              <p className="text-sm text-zinc-400">None of your gladiators are ready to fight right now (all resting or none recruited).</p>
            ) : (
              <>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Your Gladiator</label>
                <select data-testid="challenge-my-glad" value={myGlad} onChange={(e) => setMyGlad(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 text-zinc-200 px-3 py-2.5 mt-1 mb-4 outline-none focus:border-amber-600">
                  <option value="">— select —</option>
                  {mine.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.cls}, {x.rating})</option>)}
                </select>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Tactics</label>
                <select data-testid="challenge-tactics" value={tactics} onChange={(e) => setTactics(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 text-zinc-200 px-3 py-2.5 mt-1 mb-5 outline-none focus:border-amber-600">
                  {TACTICS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </>
            )}
            <div className="flex gap-2">
              <button className={btn("ghost") + " flex-1"} onClick={() => setChalOpen(false)}>Cancel</button>
              <button data-testid="send-challenge-btn" disabled={busy || mine.length === 0} className={btn() + " flex-1"} onClick={sendChallenge}>
                <span className="flex items-center justify-center gap-2"><Swords size={15} /> Send Challenge</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <BattleReplay battleId={replayId} open={!!replayId} onClose={() => setReplayId(null)} shareable />

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} kind="gladiator" id={gladId}
        text={`Meet ${g.name}, my ${g.cls} gladiator on Lanista — ${g.wins}W/${g.losses}L. Think you can beat them?`} />

      {bountyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setBountyOpen(false)}>
          <div className="bg-zinc-950 border border-yellow-800/50 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-cinzel text-xl text-yellow-300 uppercase tracking-wide mb-2 flex items-center gap-2"><GoldCoin size={18} /> Bounty on {g.name}</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Put up gold now (it’s held in escrow). If {g.name} loses any ranked fight during Bounty Week, the victor claims your gold. Unclaimed bounties are refunded when the event ends.
              {bounty?.my_bounty > 0 && <span className="block mt-2 text-yellow-400/80">You already have {bounty.my_bounty}g on this gladiator.</span>}
            </p>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Bounty Amount (gold)</label>
            <input data-testid="bounty-amount-input" type="number" min={bounty?.min_bounty || 1} value={bountyAmt} onChange={(e) => setBountyAmt(e.target.value)}
              placeholder={`min ${bounty?.min_bounty || 1}`}
              className="w-full bg-zinc-900 border border-zinc-800 text-yellow-200 px-3 py-2.5 mt-1 mb-2 outline-none focus:border-yellow-600" />
            <p className="text-[11px] text-zinc-500 mb-5">Your gold: <span className="text-yellow-300">{bounty?.gold ?? 0}</span></p>
            <div className="flex gap-2">
              <button className={btn("ghost") + " flex-1"} onClick={() => setBountyOpen(false)}>Cancel</button>
              <button data-testid="confirm-bounty-btn" disabled={busy} onClick={placeBounty}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-yellow-600 text-zinc-950 hover:bg-yellow-500 disabled:opacity-50">
                <GoldCoin size={15} /> {busy ? "Placing…" : "Place Bounty"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instant Scout Confirmation Modal */}
      <Modal open={instantScoutOpen} onClose={() => setInstantScoutOpen(false)} title="Use Instant Scout">
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-orange-950/20 border border-orange-800/40 rounded-sm">
            <Scroll className="text-orange-400" size={32} />
            <div>
              <h3 className="font-cinzel text-amber-300 text-sm">Instant Scout</h3>
              <p className="text-xs text-zinc-400">Fully reveal gladiator's potential (100%)</p>
            </div>
          </div>

          <div>
            <p className="text-sm text-zinc-300 mb-2">
              This will consume 1 Instant Scout scroll to fully reveal <span className="text-amber-400 font-semibold">{data.gladiator.name}</span>'s potential.
            </p>
            {data.gladiator.potential_band?.percent > 0 && data.gladiator.potential_band?.percent < 100 && (
              <div className="p-3 bg-amber-900/20 border border-amber-700/40 rounded-sm">
                <p className="text-xs text-amber-300">
                  ⚠ This gladiator is already <span className="font-semibold">{data.gladiator.potential_band.percent}% scouted</span>. Using this item will instantly reveal the remaining unknown potential.
                </p>
              </div>
            )}
          </div>

          <div className="text-xs text-zinc-500">
            <p>• Current potential band: <span className="text-orange-400">{data.gladiator.potential_band?.label}</span></p>
            <p>• After use: Exact potential value revealed</p>
            <p>• Remaining scrolls: <span className="text-amber-400">{instantScoutCount - 1}</span></p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setInstantScoutOpen(false)}
              className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              onClick={useInstantScout}
              className={btn() + " flex-1 bg-orange-600 hover:bg-orange-500"}
              disabled={busy}
            >
              {busy ? "Using..." : "Use Instant Scout"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
